import React, { useMemo } from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import Heading from 'components/shared/heading';
import Button from 'components/shared/button';
import PhoneIcon from 'components/icons/Phone';
import ArrowRightIcon from 'components/icons/ArrowRight';
import TeamIcon from 'components/icons/Team';
import ClockBackIcon from 'components/icons/ClockBack';
import ReviewIcon from 'components/icons/Review';
import WorkflowIcon from 'components/icons/Workflow';
import StorageIcon from 'components/icons/Storage';
import LeafIcon from 'components/icons/Leaf';
import ChatBotIcon from 'components/icons/ChatBot';
import CardAccessIcon from 'components/icons/CardAccess';
import FaceRecognitionIcon from 'components/icons/FaceRecognition';
import PinCodeIcon from 'components/icons/PinCode';
import ArchiveDownIcon from 'components/icons/ArchiveDown';
import PeopleDistanceIcon from 'components/icons/PeopleDistance';
import styles from './styles.module.scss';

const MOTION_EASE = [0.43, 0.13, 0.23, 0.96]; // Smoother ease-out curve

const gridVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.25,
      delayChildren: 0.3,
    },
  },
};

const cardVariants = {
  hidden: {
    opacity: 0,
    y: 40,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.9,
      ease: MOTION_EASE,
    },
  },
};

const buttonVariants = {
  hidden: {
    opacity: 0,
    y: 25,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: MOTION_EASE,
      delay: 0.5,
    },
  },
};

const getIcon = (icon) => {
  switch (icon) {
    case 'team':
      return <TeamIcon />;
    case 'clock-back':
      return <ClockBackIcon />;
    case 'review':
      return <ReviewIcon />;
    case 'workflow':
      return <WorkflowIcon />;
    case 'storage':
      return <StorageIcon />;
    case 'leaf':
      return <LeafIcon />;
    case 'chat-bot':
      return <ChatBotIcon />;
    case 'card-access':
      return <CardAccessIcon />;
    case 'face-recognition':
      return <FaceRecognitionIcon />;
    case 'pin-code':
      return <PinCodeIcon />;
    case 'archive-down':
      return <ArchiveDownIcon className={styles.svgIcon} />;
    case 'people-distance':
      return <PeopleDistanceIcon />;
    default:
      return null;
  }
};
const CardsNew = ({
  title = null,
  titleTag = 'h2',
  buttonLabel = '',
  buttonLink = '',
  theme = '',
  cards = [],
}) => {
  const isPhone = useMemo(() => buttonLink?.includes('tel:'), [buttonLink]);
  const [sectionRef, inView] = useInView({ threshold: 0.2, triggerOnce: false });

  return (
    <section
      ref={sectionRef}
      className={cn(styles.cardsNew, {
        [styles.themeLight]: theme === 'light',
        [styles.themeDark]: theme === 'dark',
      })}
    >
      <div className="container">
        <div className={styles.header}>
          {title && (
            <Heading className={styles.title} tag={titleTag} size="xl">
              {title}
            </Heading>
          )}
        </div>

        <motion.div
          className={styles.grid}
          variants={gridVariants}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
        >
          {cards.map((card, index) => {
            const Icon = getIcon(card.icon);
            return (
              <motion.div key={index} className={styles.card} variants={cardVariants}>
                {Icon && <div className={styles.iconWrapper}>{Icon}</div>}
                <div className={styles.content}>
                  <h4 className={styles.cardTitle}>{card.title}</h4>
                  <div dangerouslySetInnerHTML={{ __html: card.description }} />
                </div>
              </motion.div>
            );
          })}
        </motion.div>
        {buttonLabel && buttonLink && (
          <motion.div
            className={styles.btnBox}
            variants={buttonVariants}
            initial="hidden"
            animate={inView ? 'visible' : 'hidden'}
          >
            <Button
              theme="accent-primary"
              to={buttonLink}
              className={cn(styles.btn, { [styles.btnPhone]: isPhone })}
            >
              {isPhone && <PhoneIcon />}
              {buttonLabel}
              {!isPhone && <ArrowRightIcon />}
            </Button>
          </motion.div>
        )}
      </div>
    </section>
  );
};

CardsNew.propTypes = {
  title: PropTypes.string,
  titleTag: PropTypes.oneOf(['h1', 'h2', 'h3']),
  buttonLabel: PropTypes.string,
  buttonLink: PropTypes.string,
  theme: PropTypes.string,
  cards: PropTypes.arrayOf(
    PropTypes.shape({
      icon: PropTypes.node,
      title: PropTypes.string,
      description: PropTypes.string,
    })
  ),
};

export default CardsNew;
